import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReportGenerator } from '../components/reports/ReportGenerator';
import { CampaignPicker } from '../components/reports/CampaignPicker';
import { ReportPreview } from '../components/reports/ReportPreview';
import type { ReportInput, ReportRecord, ReportTemplate } from '../../shared/types/reports';
import { reportsService } from '../../backend/services/reports';
import { reportDataService, type MetricsData } from '../../backend/services/reportData';
import { metaAdsService } from '../../backend/services/metaAds';
import { useContacts } from '../hooks/useContacts';
import { useAuth } from '../hooks/useAuth';
import { useCampaigns } from '../hooks/useCampaigns';
import type { Client, Campaign } from '../../shared/types/entities';
import { aiNarrativeService, type NarrativeBullet } from '../../backend/services/aiNarrative';
import { integrationSettings } from '../../backend/services/integrationSettings';
// Upload UIs live on Releases; keep Reports focused on reporting
import { ReleasesNavigator } from '../utils/releasesNavigation';
// supabase is not used directly on Reports anymore
import React from 'react';
import PageHeader from '../components/PageHeader';
import { FileText } from 'lucide-react';
import { ReportCanvas } from '../components/reports/ReportCanvas';
import { InsightElement, ReportStatus, AuditEntry } from '../shared/types/reports';

// Recent uploads panel removed from Reports to keep IA clean

function composeHtml(
  input: ReportInput,
  metrics: MetricsData,
  ctx: { client?: Client | null; campaigns?: Campaign[] },
  narrative: { summary?: string; bullets?: NarrativeBullet[] },
  authorName?: string
): string {
  // Ensure YYYY-MM-DD is treated as a local date (not UTC) to avoid off-by-one display issues
  const parseLocal = (d: string) => {
    const [y, m, day] = d.split('-').map(Number);
    return new Date(y, (m || 1) - 1, day || 1);
  };
  const fmtDate = (d: string) => parseLocal(d).toLocaleDateString();
  const fmtNum = (n: any, opts?: Intl.NumberFormatOptions) => {
    if (typeof n !== 'number' || !isFinite(n)) return '—';
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2, ...opts }).format(n);
  };

  const clientName = ctx.client?.name || 'Break Records Client';
  const campaignNames = (ctx.campaigns || []).filter(c => input.campaignIds.includes(c.id) && c.name).map(c => c.name);
  const primaryCampaign = campaignNames[0] || 'Campaign Analysis';
  const days = Math.max(1, Math.round((parseLocal(input.endDate).getTime() - parseLocal(input.startDate).getTime()) / 86400000));
  const periodLabel = days === 28 ? 'Last 28 Days' : `${fmtDate(input.startDate)} – ${fmtDate(input.endDate)}`;
  const platformsLabel = [
    input.include.meta ? 'Meta Ads' : null,
    input.include.hypeedit ? 'Hypeddit Landing Page' : null,
    input.include.spotify ? 'Spotify (context)' : null,
  ].filter(Boolean).join(' + ');

  // Meta metrics (available subset)
  const spend = metrics.meta?.spend;
  const impressions = metrics.meta?.impressions;
  const clicksAll = metrics.meta?.clicks;
  const linkClicks = clicksAll; // until link_clicks is available
  const reach = undefined;
  const results = undefined;
  const ctr = metrics.meta?.ctr; // %
  const cpr = metrics.meta?.cpr; // cost per result (proxy)
  const cpcAll = clicksAll && clicksAll > 0 && spend ? spend / clicksAll : undefined;
  const cpcLink = linkClicks && linkClicks > 0 && spend ? spend / linkClicks : undefined;
  const frequency = impressions && reach ? impressions / reach : undefined;
  const cpm = impressions && impressions > 0 && spend ? spend / (impressions / 1000) : undefined;

  // Hypeddit landing page (optional; depends on parsed uploads)
  const visits = (metrics as any)?.hypeddit?.visits;
  const lpLinkClicks = (metrics as any)?.hypeddit?.linkClicks;
  const engagementRate = visits && visits > 0 && lpLinkClicks ? lpLinkClicks / visits : (metrics as any)?.hypeddit?.engagement_rate;

  const h = (txt: string) => `<h1 style="margin:0 0 8px; font:700 30px/1.2 -apple-system,BlinkMacSystemFont,'SF Pro Display',Segoe UI,Roboto,Helvetica,Arial,sans-serif; color:#1d1d1f; letter-spacing:-0.5px">${txt}</h1>`;
  const h3 = (txt: string) => `<h3 style="margin:16px 0 8px; font:600 16px/1.4 -apple-system,BlinkMacSystemFont,'SF Pro Text',Segoe UI,Roboto,Helvetica,Arial,sans-serif; color:#424245">${txt}</h3>`;
  const p = (txt: string) => `<p style="margin:0 0 12px; font:400 16px/1.5 -apple-system,BlinkMacSystemFont,'SF Pro Text',Segoe UI,Roboto,Helvetica,Arial,sans-serif; color:#1d1d1f">${txt}</p>`;
  const caption = (txt: string) => `<p style="margin:0; font:400 14px/1.4 -apple-system,BlinkMacSystemFont,'SF Pro Text',Segoe UI,Roboto,Helvetica,Arial,sans-serif; color:#86868b">${txt}</p>`;
  const table = (rows: Array<[string, string]>) => `
    <table style="border-collapse:collapse; width:100%; margin:0 0 24px; font:400 15px/1.4 -apple-system,BlinkMacSystemFont,'SF Pro Text',Segoe UI,Roboto,Helvetica,Arial,sans-serif; border-radius:8px; overflow:hidden; border:1px solid #d2d2d7"><tbody>
      ${rows.map(([k, v]) => `<tr><td style="padding:12px 16px; border-bottom:1px solid #f5f5f7; background:#fafafa; font-weight:500; color:#424245">${k}</td><td style="padding:12px 16px; border-bottom:1px solid #f5f5f7; color:#1d1d1f">${v}</td></tr>`).join('')}
    </tbody></table>`;

  const summaryText = (narrative.summary && narrative.summary.trim()) || (input.introText || '');
  const summaryBlock = summaryText ? `${h3('Executive Summary')}${p(summaryText)}` : '';

  const campaignOverviewBlock = `
    ${h3('Campaign Performance Overview')}
    ${table([
      ['Total Spend', spend != null ? fmtNum(spend, { style: metrics.meta?.currency ? 'currency' : undefined, currency: metrics.meta?.currency || undefined }) : '—'],
      ['Cost per Result', cpr != null ? fmtNum(cpr, { style: metrics.meta?.currency ? 'currency' : undefined, currency: metrics.meta?.currency || undefined }) : '—'],
      ['CTR (Link Click-Through Rate)', ctr != null ? `${fmtNum(ctr)}%` : '—'],
      ['Frequency', frequency != null ? fmtNum(frequency) : '—'],
      ['CPC (All)', cpcAll != null ? fmtNum(cpcAll, { style: metrics.meta?.currency ? 'currency' : undefined, currency: metrics.meta?.currency || undefined }) : '—'],
      ['Results', results != null ? fmtNum(results) : '—'],
      ['Total Link Clicks', linkClicks != null ? fmtNum(linkClicks) : '—'],
      ['Reach', reach != null ? fmtNum(reach) : '—'],
      ['CPM (Cost per 1,000 Impressions)', cpm != null ? fmtNum(cpm, { style: metrics.meta?.currency ? 'currency' : undefined, currency: metrics.meta?.currency || undefined }) : '—'],
      ['CPC (Cost per Link Click)', cpcLink != null ? fmtNum(cpcLink, { style: metrics.meta?.currency ? 'currency' : undefined, currency: metrics.meta?.currency || undefined }) : '—'],
    ])}
  `;

  const lpBlock = input.include.hypeedit ? `
    ${h3('Landing Page Performance (Hypeddit)')}
    ${table([
      ['Total Visits', visits != null ? fmtNum(visits) : '—'],
      ['Total Link Clicks', lpLinkClicks != null ? fmtNum(lpLinkClicks) : '—'],
      ['Overall Engagement Rate', engagementRate != null ? `${fmtNum((engagementRate as number) * 100)}%` : '—'],
    ])}
  ` : '';

  const countries = (metrics as any)?.hypeddit?.topCountries as Array<{ country: string; visits: number; linkClicks: number; engagementRate: number }> | undefined;
  const sources = (metrics as any)?.hypeddit?.topSources as Array<{ source: string; visits: number; linkClicks: number; engagementRate: number }> | undefined;
  const renderRankTable = (title: string, cols: string[], rows: string[][]) => `
    ${h3(title)}
    <table style="border-collapse:collapse; width:100%; margin:0 0 24px; font:400 15px/1.4 -apple-system,BlinkMacSystemFont,'SF Pro Text',Segoe UI,Roboto,Helvetica,Arial,sans-serif; border-radius:8px; overflow:hidden; border:1px solid #d2d2d7">
      <thead><tr>${cols.map(c => `<th style="padding:12px 16px; background:#f5f5f7; text-align:left; font-weight:600; color:#424245; border-bottom:1px solid #d2d2d7">${c}</th>`).join('')}</tr></thead>
      <tbody>${rows.map(r => `<tr>${r.map((c,i)=>`<td style="padding:12px 16px; border-bottom:1px solid #f5f5f7; ${i===0?'font-weight:500; color:#424245':'color:#1d1d1f'}">${c}</td>`).join('')}</tr>`).join('')}</tbody>
    </table>
  `;
  const countriesRows = (countries || []).map((r, idx) => [String(idx+1), r.country, fmtNum(r.visits), fmtNum(r.linkClicks), `${fmtNum(r.engagementRate*100)}%`]);
  const sourcesRows = (sources || []).map(r => [r.source, fmtNum(r.visits), fmtNum(r.linkClicks), `${fmtNum(r.engagementRate*100)}%`]);
  const topCountriesBlock = renderRankTable('Top Countries by Engagement', ['Rank','Country','Visits','Link Clicks','Engagement Rate'], countriesRows);
  const topSourcesBlock = renderRankTable('Top Traffic Sources', ['Source','Visits','Link Clicks','Engagement Rate'], sourcesRows);

  const bullets = (narrative.bullets && narrative.bullets.length ? narrative.bullets : (input.insightsBullets || []).map(b => ({ tone: 'neutral' as const, title: b, evidence: '' })));
  const insightsBlock = bullets.length ? `
    ${h3('Key Insights & Recommendations')}
    <ul style="margin:0 0 24px 0; padding:0; list-style:none">
      ${bullets.map(b => `<li style="margin:0 0 12px; padding:16px; background:#f5f5f7; border-radius:8px; font:400 15px/1.5 -apple-system,BlinkMacSystemFont,'SF Pro Text',Segoe UI,Roboto,Helvetica,Arial,sans-serif">
        <span style="display:inline-block; padding:2px 8px; margin-right:8px; background:${b.tone === 'positive' ? '#d1f2eb' : b.tone === 'negative' ? '#fadbd8' : '#e8f4fd'}; color:${b.tone === 'positive' ? '#0e5d3b' : b.tone === 'negative' ? '#922b21' : '#1e3a8a'}; border-radius:4px; font-size:13px; font-weight:500; text-transform:uppercase; letter-spacing:0.5px">${b.tone}</span>
        <strong style="color:#1d1d1f; font-weight:600">${b.title}</strong>${b.evidence ? `<br><span style="color:#424245; margin-top:4px; display:block">${b.evidence}</span>` : ''}
      </li>`).join('')}
    </ul>
  ` : '';

  const metaFooter = `<div style="margin-top:32px; padding-top:24px; border-top:1px solid #d2d2d7; font:400 14px/1.4 -apple-system,BlinkMacSystemFont,'SF Pro Text',Segoe UI,Roboto,Helvetica,Arial,sans-serif; color:#86868b">
    Generated for ${clientName} • ${new Date().toLocaleString()}${authorName ? ` • By ${authorName}` : ''}
  </div>`;

  const container = `
    <div style="max-width:800px; margin:24px auto; padding:48px; background:#ffffff; color:#1d1d1f; border-radius:12px; box-shadow:0 4px 24px rgba(0,0,0,0.08)">
      <div style="margin-bottom:32px; padding-bottom:24px; border-bottom:1px solid #d2d2d7">
        ${h(`${clientName}`)}
        ${p(`<strong style="font-weight:600; color:#424245">${primaryCampaign}</strong>`)}
        ${caption(`${periodLabel} • ${platformsLabel || '—'}`)}
      </div>
      ${summaryBlock}
      ${campaignOverviewBlock}
      ${lpBlock}
      ${topCountriesBlock}
      ${topSourcesBlock}
      ${insightsBlock}
      ${metaFooter}
    </div>`;

  return `<!doctype html><html><head><meta charset='utf-8'><meta name='color-scheme' content='light dark'><meta name="viewport" content="width=device-width, initial-scale=1" /></head><body style="background:#f6f6f6; margin:0; padding:16px">${container}</body></html>`;
}

export const Reports: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'build' | 'templates' | 'finalized'>('build');
  const [input, setInput] = useState<ReportInput>({
    clientId: '',
    campaignIds: [],
    startDate: (() => { const d=new Date(Date.now()-7*24*3600e3); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(),
    endDate: (() => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(),
    include: { meta: true, spotify: true, hypeedit: false },
    introText: '',
    insightsBullets: [],
  });
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [archive, setArchive] = useState<ReportRecord[]>([]);
  const [metrics, setMetrics] = useState<MetricsData & { wowDeltas?: Record<string, number>; projections?: Record<string, any> }>({ dataFreshness: {} });
  const [aiSummary, setAiSummary] = useState<string>('');
  const [aiBullets, setAiBullets] = useState<NarrativeBullet[]>([]);
  const [aiLoading, setAiLoading] = useState<boolean>(false);
  const [aiLocked, setAiLocked] = useState<boolean>(false);
  const [aiError, setAiError] = useState<string>('');
  const [aiDeterministic, setAiDeterministic] = useState<boolean>(true);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const { contacts } = useContacts({ sortBy: 'name', sortOrder: 'asc' });
  const { campaigns } = useCampaigns({});
  const { user } = useAuth();
  const clients = contacts; // Alias for backward compatibility
  const clientSelected: Client | undefined = clients.find(c => c.id === input.clientId);
  const html = useMemo(
    () => composeHtml(input, metrics, { client: clientSelected, campaigns }, { summary: aiSummary, bullets: aiBullets }, 'user@example.com'),
    [input, metrics, clientSelected?.id, aiSummary, aiBullets, campaigns.map(c => c.id).join(',')]
  );
  const [status, setStatus] = useState<string>('');
  const [clientQuery, setClientQuery] = useState('');
  // campaign query handled inside CampaignPicker
  const [showSourcesDrawer, setShowSourcesDrawer] = useState(false);

  // New state for redesign (renamed to avoid conflict)
  const [layout, setLayout] = useState<InsightElement[]>([]);
  const [reportStatus, setReportStatus] = useState<ReportStatus>('draft');
  const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);

  // New for layout: Collapsible palette and audit modal
  const [showAudit, setShowAudit] = useState(false);
  const [showElementPicker, setShowElementPicker] = useState(false);

  // Placeholder handlers (updated refs)
  const handleLayoutChange = React.useCallback((newLayout: InsightElement[]) => {
    setLayout(newLayout);
    // Log audit
    setAuditLog(prev => [...prev, {
      user_id: user?.id || '',
      timestamp: new Date().toISOString(),
      action: 'updated_layout',
      summary: `Layout changed to ${newLayout.length} elements`
    }]);
  }, [user?.id]);

  const handleFinalize = () => {
    if (reportStatus === 'draft') {
      setReportStatus('finalized');
      setAuditLog(prev => [...prev, { user_id: user?.id || '', timestamp: new Date().toISOString(), action: 'finalized', summary: 'Report finalized' }]);
      // TODO: API call to /finalize
    } else {
      setReportStatus('draft');
    }
  };

  const handleCloneWeekly = async () => {
    if (!input.clientId) return;
    try {
      const response = await fetch(`/api/reports/${input.clientId}/history?limit=1&status=finalized`);  // Stub endpoint param
      const history = await response.json();
      if (history.main?.layout) {
        setLayout(history.main.layout);
        setAuditLog(prev => [...prev, { user_id: user?.id || '', timestamp: new Date().toISOString(), action: 'cloned_weekly', summary: `Cloned layout from ${history.main.id}` }]);
      }
    } catch (e) {
      console.error('Clone failed:', e);
    }
  };

  const handleDragStart = (event: React.DragEvent, elementType: string) => {
    event.dataTransfer.setData('elementType', elementType);
    // TODO: Full DnD integration
  };

  const handleViewAudit = () => {
    setShowAudit(true);
  };

  // Keyboard shortcuts
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only trigger if we're in draft mode and no input is focused
      if (reportStatus === 'draft' && !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement)?.tagName)) {
        if (e.key === '/') {
          e.preventDefault();
          setShowElementPicker(true);
        } else if (e.key === 'Escape' && showElementPicker) {
          e.preventDefault();
          setShowElementPicker(false);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [reportStatus, showElementPicker]);

  useEffect(() => {
    (async () => {
      const [tpls, reports] = await Promise.all([
        reportsService.listTemplates({ clientId: undefined }),
        reportsService.listReports({ limit: 10 }),
      ]);
      setTemplates(tpls);
      setArchive(reports);
    })();
  }, []);


  const onGenerate = async () => {
    if (!input.campaignIds.length) return;
    
    setLoading(true);
    
    try {
      const data = await reportDataService.getAggregatedMetrics(
        input.campaignIds,
        input.startDate,
        input.endDate,
        input.clientId || undefined
      );
      setMetrics(data);

      // Sprint 1: Auto-populate sample elements
      if (data.meta && layout.length === 0) {
        const sampleElements: InsightElement[] = [
          {
            type: 'text',
            config: { 
              title: 'Executive Summary',
              text: 'Campaign performance overview for the selected period...',
              id: 'text-summary'
            }
          },
          {
            type: 'kpi',
            dataKey: 'total_spend',
            config: { 
              title: 'Total Spend',
              dataKey: 'total_spend',
              label: 'Total Spend',
              id: 'kpi-spend'
            }
          },
          {
            type: 'table',
            dataKey: 'campaign_performance',
            config: { 
              title: 'Campaign Performance Overview',
              id: 'table-performance'
            }
          }
        ];
        setLayout(sampleElements);
      }
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
    } finally {
      setLoading(false);
}
  };

  const onSyncData = async () => {
    if (!input.campaignIds.length) return;
    setSyncing(true);
    setStatus('');
    try {
      // Use selected date range; expand to hour boundaries
      const since = new Date(`${input.startDate}T00:00:00`);
      const until = new Date(`${input.endDate}T23:00:00`);
      const sinceISO = since.toISOString();
      const untilISO = until.toISOString();

      // For each selected campaign, fetch mock/real spend via proxy and ingest with campaign_id
      let totalInserted = 0;
      for (const cid of input.campaignIds) {
        const hours = await metaAdsService.fetchHourlySpend(sinceISO, untilISO);
        const res = await metaAdsService.ingestHourlySpend(hours, cid);
        totalInserted += res.inserted || 0;
      }
      setStatus(`Synced ${totalInserted} hours`);
      // Refresh metrics after sync
      await onGenerate();
    } catch (e) {
      console.error('Sync failed', e);
      setStatus('Sync failed');
    } finally {
      setTimeout(() => setStatus(''), 2000);
      setSyncing(false);
    }
  };

  const onGenerateAI = async () => {
    if (aiLocked) return;
    setAiLoading(true);
    setAiError('');
    try {
      // Load OpenAI credentials from Supabase/local fallback
      const openaiCfg = await integrationSettings.get('openai');
      const apiKey = openaiCfg?.openai_api_key || (import.meta as any).env?.VITE_OPENAI_API_KEY;
      const model = openaiCfg?.openai_model || 'gpt-4.1-mini';
      if (!apiKey) {
        throw new Error('OpenAI not configured. Add your API key in Settings → Integrations.');
      }

      const campaignNames = (campaigns || []).filter(c => input.campaignIds.includes(c.id) && c.name).map(c => c.name);
      const kpis: Record<string, number | string | undefined> = {
        spend: metrics.meta?.spend,
        cost_per_result: metrics.meta?.cpr,
        ctr_link: metrics.meta?.ctr,
        frequency: undefined,
        cpc_all: metrics.meta?.spend && metrics.meta?.clicks ? metrics.meta.spend / metrics.meta.clicks : undefined,
        results: undefined,
        link_clicks: metrics.meta?.clicks,
        reach: undefined,
        cpm: metrics.meta?.spend && metrics.meta?.impressions ? metrics.meta.spend / (metrics.meta.impressions / 1000) : undefined,
        cpc_link: undefined,
      };
      const ctx = {
        clientName: clientSelected?.name,
        campaignName: campaignNames[0],
        periodLabel: (() => {
          const parseLocal = (d: string) => {
            const [y, m, day] = d.split('-').map(Number);
            return new Date(y, (m || 1) - 1, day || 1);
          };
          const days = Math.max(1, Math.round((parseLocal(input.endDate).getTime() - parseLocal(input.startDate).getTime()) / 86400000));
          return days === 28 ? 'Last 28 Days' : `${parseLocal(input.startDate).toLocaleDateString()} – ${parseLocal(input.endDate).toLocaleDateString()}`;
        })(),
        kpis,
        landing: input.include.hypeedit ? {
          visits: (metrics as any)?.hypeddit?.visits,
          linkClicks: (metrics as any)?.hypeddit?.linkClicks,
          engagementRate: (metrics as any)?.hypeddit?.engagement_rate,
          topCountries: (metrics as any)?.hypeddit?.topCountries,
          topSources: (metrics as any)?.hypeddit?.topSources,
        } : undefined,
  };
  const out = await aiNarrativeService.generate(ctx, { promptVersion: 'v1', temperature: 0.2, seed: aiDeterministic ? 42 : undefined, model, apiKeyOverride: apiKey });
      setAiSummary(out.summary);
      setAiBullets(out.bullets);
    } catch (e: any) {
      setAiError(e?.message || 'Failed to generate narrative');
    } finally {
      setAiLoading(false);
    }
  };

  const onCopyFormatted = async () => {
    const toPlain = (markup: string) => {
      const doc = new DOMParser().parseFromString(markup, 'text/html');
      return doc.body.textContent || '';
    };
    try {
      const supportsClipboardWrite = typeof navigator !== 'undefined' && 'clipboard' in navigator && 'write' in navigator.clipboard;
      const supportsClipboardItem = typeof window !== 'undefined' && typeof (window as any).ClipboardItem !== 'undefined';
      if (supportsClipboardWrite && supportsClipboardItem) {
        const ClipboardItemCtor = (window as any).ClipboardItem as typeof ClipboardItem;
        const item = new ClipboardItemCtor({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([toPlain(html)], { type: 'text/plain' }),
        });
        await navigator.clipboard.write([item]);
      } else {
        // Fallback: copy formatted content using a hidden, contentEditable element
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const container = document.createElement('div');
        container.style.position = 'fixed';
        container.style.left = '-9999px';
        container.style.top = '0';
        container.setAttribute('contenteditable', 'true');
        container.innerHTML = doc.body.innerHTML;
        document.body.appendChild(container);
        const range = document.createRange();
        range.selectNodeContents(container);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
        document.execCommand('copy');
        selection?.removeAllRanges();
        document.body.removeChild(container);
      }
      setStatus('Copied formatted');
      setTimeout(() => setStatus(''), 1800);
    } catch (err) {
      // Last resort: write plain text
      try {
        await navigator.clipboard.writeText(toPlain(html));
        setStatus('Copied text');
        setTimeout(() => setStatus(''), 1800);
      } catch {}
    }
  };

  const onCopyPlain = async () => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const text = doc.body.textContent || '';
    await navigator.clipboard.writeText(text);
    setStatus('Copied text');
    setTimeout(() => setStatus(''), 1800);
  };

  const saveTemplate = async (name: string) => {
    await reportsService.saveTemplate({
      name,
      clientId: input.clientId || null,
      campaignIds: input.campaignIds,
      defaultSources: input.include,
      defaultTimeframe: { startDate: input.startDate, endDate: input.endDate },
      createdBy: null,
    });
    const tpls = await reportsService.listTemplates({});
    setTemplates(tpls);
  };

  const applyTemplate = async (tplId: string) => {
    const tpl = templates.find(t => t.id === tplId);
    if (!tpl) return;
    setInput(v => ({
      ...v,
      clientId: tpl.clientId || v.clientId,
      campaignIds: tpl.campaignIds || v.campaignIds,
      include: { ...v.include, ...(tpl.defaultSources || {}) },
      startDate: tpl.defaultTimeframe?.startDate || v.startDate,
      endDate: tpl.defaultTimeframe?.endDate || v.endDate,
    }));
  };

  const saveReport = async () => {
    const payload: Omit<ReportRecord, 'id' | 'createdAt'> = {
      clientId: input.clientId,
      campaignIds: input.campaignIds,
      startDate: input.startDate,
      endDate: input.endDate,
      include: input.include,
      html,
      metrics: { ...metrics, ai: { summary: aiSummary, bullets: aiBullets } },
      authorUserId: null,
    };
    await reportsService.saveReport(payload);
    const latest = await reportsService.listReports({ limit: 10 });
    setArchive(latest);
  };

  // Quick date presets moved into ReportGenerator to sit next to the date inputs

  const handlePrint = () => {
    try {
      const win = window.open('', '_blank');
      if (!win) return;
      win.document.open();
      win.document.write(html);
      win.document.close();
      setTimeout(() => { win.focus(); win.print(); }, 150);
    } catch {}
  };

  const handleUpdateElement = (id: string, updates: any) => {
    // Stub: Update by index (use real ID in prod)
    const idx = layout.findIndex(el => el.type === id);  // Simple match
    if (idx >= 0) {
      const newLayout = layout.map((el, i) => i === idx ? { ...el, ...updates } : el);
      setLayout(newLayout);
    }
  };

  // Simplified Apple-style document view
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Minimal Top Bar - Like Google Docs */}
      <div className="bg-white border-b border-gray-200 px-6 py-2">
        <div className="flex items-center justify-between">
          {/* Left: Client/Campaign in minimal dropdown */}
          <div className="flex items-center space-x-4">
            <select 
              value={input.clientId}
              onChange={(e) => setInput(prev => ({ ...prev, clientId: e.target.value }))}
              className="text-sm border-0 focus:ring-0 font-medium"
            >
              <option value="">Select Client...</option>
              {clients?.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            
            <select 
              value={input.campaignIds[0] || ''}
              onChange={(e) => {
                const campaignId = e.target.value;
                if (campaignId) {
                  setInput(prev => ({ 
                    ...prev, 
                    campaignIds: [campaignId] // For now, single campaign selection
                  }));
                } else {
                  setInput(prev => ({ ...prev, campaignIds: [] }));
                }
              }}
              className="text-sm border-0 focus:ring-0 font-medium"
              disabled={!input.clientId}
            >
              <option value="">Select Campaign...</option>
              {campaigns?.filter(c => {
                // Filter campaigns by selected client if possible
                const artistClient = (c as any)?.release?.artist?.client_id;
                return input.clientId ? (artistClient ? artistClient === input.clientId : true) : true;
              }).map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            
            {input.campaignIds.length > 0 && (
              <span className="text-sm text-gray-500">
                {input.campaignIds.length} campaign{input.campaignIds.length > 1 ? 's' : ''} selected
              </span>
            )}
          </div>

          {/* Right: Action buttons */}
          <div className="flex items-center space-x-2">
            {input.campaignIds.length > 0 && (
              <button
                onClick={onGenerate}
                disabled={loading}
                className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Generate Data'}
              </button>
            )}
            <button
              onClick={handleCloneWeekly}
              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
            >
              Clone Weekly
            </button>
            <button
              onClick={handleFinalize}
              className={`px-4 py-1.5 text-sm rounded-md ${
                reportStatus === 'draft' 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-gray-200 text-gray-600'
              }`}
            >
              {reportStatus === 'draft' ? 'Finalize' : 'Edit'}
            </button>
            <button
              onClick={() => setShowAudit(true)}
              className="p-1.5 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Document Canvas - Full Focus */}
      <div className="py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Document Container - Like Pages/Word */}
          <div className="bg-white rounded-lg shadow-lg min-h-[11in] p-16">
            {/* Document Header */}
            <div className="mb-8 pb-4 border-b">
              <h1 className="text-3xl font-light mb-2">
                {clientSelected?.name || 'Break Records Client'}
              </h1>
              <p className="text-gray-500">
                Campaign Analysis • {input.startDate} to {input.endDate}
              </p>
            </div>

            {/* Document Content - Direct Edit */}
            <div className="prose prose-lg max-w-none">
              {layout.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <p className="mb-4">Click the + button to add content</p>
                  <p className="text-sm">Or press <kbd className="px-2 py-1 bg-gray-200 rounded text-xs">/</kbd> anywhere to insert elements</p>
                </div>
              ) : (
                <ReportCanvas
                  layout={layout}
                  status={reportStatus}
                  onChange={handleLayoutChange}
                  editable={reportStatus === 'draft'}
                  metrics={metrics}
                  minimal={true}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Floating Add Button - Material Design FAB */}
      {reportStatus === 'draft' && (
        <div className="fixed bottom-8 right-8">
          <button
            onClick={() => setShowElementPicker(true)}
            className="w-14 h-14 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 hover:shadow-xl transition-all flex items-center justify-center"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
          <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 text-xs text-gray-500 bg-white px-2 py-1 rounded shadow-sm">
            Press <kbd className="px-1 py-0.5 bg-gray-100 rounded text-xs">/</kbd>
          </div>
        </div>
      )}

      {/* Element Picker - Floating Panel */}
      {showElementPicker && (
        <div className="fixed inset-0 bg-black/20 z-40" onClick={() => setShowElementPicker(false)}>
          <div className="fixed bottom-24 right-8 bg-white rounded-lg shadow-xl p-4 w-64" onClick={e => e.stopPropagation()}>
            <h3 className="font-medium mb-3">Add Element</h3>
            <div className="space-y-2">
              {[
                { type: 'text', label: 'Text Block', icon: 'T', dataKey: null },
                { type: 'table', label: 'Campaign Performance', icon: '⊞', dataKey: 'campaign_performance' },
                { type: 'table', label: 'Daily Spend', icon: '📊', dataKey: 'meta_spend' },
                { type: 'kpi', label: 'Total Spend', icon: '💰', dataKey: 'total_spend' },
                { type: 'kpi', label: 'Impressions', icon: '👁️', dataKey: 'impressions' },
                { type: 'kpi', label: 'CTR', icon: '📈', dataKey: 'ctr' },
                { type: 'chart', label: 'Chart', icon: '📊', dataKey: null },
              ].map(el => (
                <button
                  key={`${el.type}-${el.dataKey || 'default'}`}
                  onClick={() => {
                    const newElement: InsightElement = {
                      type: el.type as any,
                      dataKey: el.dataKey || undefined,
                      config: { 
                        title: el.label,
                        id: `${el.type}-${Date.now()}`, // Unique ID for React keys
                        ...(el.type === 'text' ? { text: 'Click to edit this text block...' } : {}),
                        ...(el.type === 'kpi' ? { dataKey: el.dataKey, label: el.label } : {})
                      },
                    };
                    setLayout([...layout, newElement]);
                    setShowElementPicker(false);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded flex items-center space-x-3"
                >
                  <span className="text-lg">{el.icon}</span>
                  <span>{el.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Audit Modal - Clean and minimal */}
      {showAudit && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl overflow-hidden">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-medium">Version History</h3>
              <button 
                onClick={() => setShowAudit(false)}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 max-h-96 overflow-auto">
              {auditLog.map((entry, i) => (
                <div key={i} className="mb-3 text-sm">
                  <div className="text-gray-500">{new Date(entry.timestamp).toLocaleString()}</div>
                  <div>{entry.summary}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;